import { ReactNode, useState } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface EntityCardProps {
  imageUrl: string | null;
  imageAlt: string;
  title: string;
  subtitle?: string;
  badges?: Array<{ label: string; variant?: "default" | "secondary" | "outline" }>;
  isFeatured?: boolean;
  featuredLabel?: string;
  children?: ReactNode;
  footer?: ReactNode;
  fallbackIcon?: ReactNode;
  aspectRatio?: "square" | "video" | "portrait";
  className?: string;
  /** Width in px for the img element (improves CLS and SEO) */
  imageWidth?: number;
  /** Height in px for the img element (improves CLS and SEO) */
  imageHeight?: number;
}

/** Default image dimensions keyed by aspect ratio */
const DEFAULT_IMAGE_DIMENSIONS: Record<string, { w: number; h: number }> = {
  square: { w: 400, h: 400 },
  video:  { w: 600, h: 338 },
  portrait: { w: 400, h: 533 },
};

export function EntityCard({
  imageUrl,
  imageAlt,
  title,
  subtitle,
  badges = [],
  isFeatured,
  featuredLabel = "Featured",
  children,
  footer,
  fallbackIcon,
  aspectRatio = "video",
  className,
  imageWidth,
  imageHeight,
}: EntityCardProps) {
  const dims = DEFAULT_IMAGE_DIMENSIONS[aspectRatio];
  const resolvedWidth  = imageWidth  ?? dims.w;
  const resolvedHeight = imageHeight ?? dims.h;
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const ratioValues = {
    square: 1,
    video: 16 / 9,
    portrait: 3 / 4,
  };

  return (
    <Card className={cn("group overflow-hidden hover:shadow-hover transition-all duration-300", className)}>
      <div className="relative overflow-hidden">
        <AspectRatio ratio={ratioValues[aspectRatio]}>
          {isLoading && imageUrl && (
            <Skeleton className="absolute inset-0 w-full h-full" />
          )}
          
          {imageUrl && !hasError ? (
            <img
              src={imageUrl}
              alt={imageAlt}
              width={resolvedWidth}
              height={resolvedHeight}
              loading="lazy"
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
              className={cn(
                "w-full h-full object-cover group-hover:scale-105 transition-transform duration-300",
                isLoading ? "blur-placeholder opacity-0" : "blur-loaded opacity-100"
              )}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-secondary to-secondary/80 flex items-center justify-center">
              {fallbackIcon || (
                <span className="text-secondary-foreground/30 text-4xl font-bold">
                  {title.charAt(0)}
                </span>
              )}
            </div>
          )}
        </AspectRatio>

        {isFeatured && (
          <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground">
            {featuredLabel}
          </Badge>
        )}
      </div>

      <CardContent className="p-6">
        <h3 className="font-display text-xl font-semibold text-secondary mb-1 group-hover:text-primary transition-colors">
          {title}
        </h3>
        
        {subtitle && (
          <p className="text-primary font-medium mb-2">{subtitle}</p>
        )}

        {badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {badges.map((badge, index) => (
              <Badge key={index} variant={badge.variant || "outline"}>
                {badge.label}
              </Badge>
            ))}
          </div>
        )}

        {children}

        {footer && <div className="mt-4">{footer}</div>}
      </CardContent>
    </Card>
  );
}

// Skeleton version for loading states
export function EntityCardSkeleton({ aspectRatio = "video" }: { aspectRatio?: "square" | "video" | "portrait" }) {
  const ratioValues = {
    square: 1,
    video: 16 / 9,
    portrait: 3 / 4,
  };

  return (
    <Card className="overflow-hidden">
      <AspectRatio ratio={ratioValues[aspectRatio]}>
        <Skeleton className="w-full h-full" />
      </AspectRatio>
      <CardContent className="p-6 space-y-4">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-10 w-full" />
      </CardContent>
    </Card>
  );
}
